import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Checkbox, Typography, Paper, Grid, IconButton, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchMultipleEntriesByIds } from '../entriesApi'; // Import the API function

const LayoutSetEditPopup = ({
  open,
  batchId,
  layoutsInBatch,
  onClose,
  onRegenerate
}) => {
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());
  const [userInstructions, setUserInstructions] = useState('');
  const [allPhotosInBatch, setAllPhotosInBatch] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false); // State for loading photos

  // Helper to get a stable unique ID for a photo object
  // Handles photos from processEntryForStoryBookInternal (in layouts) and fetchMultipleEntriesByIds (raw)
  const getStablePhotoId = (photo) => {
    if (photo.mediaId !== undefined && photo.isDriveFile !== undefined) {
      // Photo object is from processEntryForStoryBookInternal (already in a layout)
      // photo.mediaId is the original GPhotos ID or Drive File ID
      return photo.isDriveFile ? `drive-${photo.mediaId}` : `gphoto-${photo.mediaId}`;
    } else if (photo.driveFileId) {
      // Photo object is from fetchMultipleEntriesByIds and is a Drive file
      // photo.driveFileId is the Drive File ID
      return `drive-${photo.driveFileId}`;
    } else {
      // Photo object is from fetchMultipleEntriesByIds and is a Google Photo
      // photo.id here is the Google Photos media ID
      return `gphoto-${photo.id}`;
    }
  };

  useEffect(() => {
    if (open && layoutsInBatch && layoutsInBatch.length > 0) {
      const fetchAllPhotosForEntries = async () => {
        setLoadingPhotos(true);
        try {
          // Consolidate all unique source_entry_ids from all layouts in the batch
          const allSourceEntryIdsInBatch = layoutsInBatch.reduce((acc, layout) => {
            if (layout.source_entry_ids) {
              layout.source_entry_ids.forEach(id => acc.add(id));
            }
            return acc;
          }, new Set());

          const uniqueSourceEntryIds = Array.from(allSourceEntryIdsInBatch);

          let photosFromFetchedEntries = [];
          if (uniqueSourceEntryIds.length > 0) {
            // Fetch full entries to get all their photos
            const fetchedEntries = await fetchMultipleEntriesByIds(uniqueSourceEntryIds);
            const rawFlattenedPhotos = fetchedEntries.flatMap(entry => entry.photos || []);

            // Ensure unique photos based on stable ID
            const uniquePhotoMap = new Map();
            rawFlattenedPhotos.forEach(p => {
              const stableId = getStablePhotoId(p);
              if (!uniquePhotoMap.has(stableId)) {
                uniquePhotoMap.set(stableId, p);
              }
            });
            photosFromFetchedEntries = Array.from(uniquePhotoMap.values());
          }
          setAllPhotosInBatch(photosFromFetchedEntries);

          // Determine initially selected photos based on photos already present in the layouts
          const initialStableIds = new Set();
          layoutsInBatch.forEach(layout => {
            layout.photos.forEach(photoInLayout => { // photoInLayout has been processed by processEntryForStoryBookInternal
              initialStableIds.add(getStablePhotoId(photoInLayout));
            });
          });
          setSelectedPhotoIds(initialStableIds);

        } catch (error) {
          console.error("Error fetching all photos for LayoutSetEditPopup:", error);
          setAllPhotosInBatch([]); // Clear photos on error
        } finally {
          setLoadingPhotos(false);
        }
      };

      fetchAllPhotosForEntries();
      setUserInstructions(''); // Reset instructions textarea
    } else if (!open) {
      // Reset state when dialog is closed
      setAllPhotosInBatch([]);
      setSelectedPhotoIds(new Set());
      setUserInstructions('');
      setLoadingPhotos(false);
    }
  }, [open, layoutsInBatch, batchId]); // batchId included in deps for completeness

  const handleTogglePhoto = (photoFromGrid) => { // photoFromGrid is from allPhotosInBatch
    const stableId = getStablePhotoId(photoFromGrid);
    setSelectedPhotoIds(prev => {
      const next = new Set(prev);
      if (next.has(stableId)) next.delete(stableId);
      else next.add(stableId);
      return next;
    });
  };

  const handleRegenerateClick = () => {
    // Filter allPhotosInBatch to get the actual photo objects that are selected
    const chosenPhotos = allPhotosInBatch.filter(p => {
      const stableId = getStablePhotoId(p);
      return selectedPhotoIds.has(stableId);
    });
    // Get source_entry_ids (can be from the first layout, assuming they are consistent for the batch)
    const sourceEntryIds = layoutsInBatch.length > 0 ? layoutsInBatch[0].source_entry_ids : [];
    onRegenerate(batchId, chosenPhotos, userInstructions, sourceEntryIds);
  };

  if (!open) return null;

  const journalText = layoutsInBatch
    .map(l => `${l.left_page_text || ''} ${l.right_page_text || ''}`.trim())
    .filter(Boolean)
    .join('\n\n');

  const aiExplanation = layoutsInBatch
    .map(l => l.explanation)
    .filter(Boolean)
    .join('\n---\n') || 'No explanation provided.';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle>
        Edit AI Layout Set ({layoutsInBatch.length} layout{layoutsInBatch.length !== 1 ? 's' : ''})
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Original Journal Text Used:</Typography>
            <Paper variant="outlined" sx={{ p: 1, overflowY: 'auto', flexGrow: 1, whiteSpace: 'pre-wrap', mb: 1, maxHeight: '25vh' }}>
              {journalText || 'No text was prominent in these layouts.'}
            </Paper>
            <Typography variant="subtitle1" gutterBottom>AI Explanation:</Typography>
            <Paper variant="outlined" sx={{ p: 1, overflowY: 'auto', flexGrow: 1, whiteSpace: 'pre-wrap', maxHeight: '25vh' }}>
              {aiExplanation}
            </Paper>
          </Grid>
          <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Select Photos for Regeneration ({selectedPhotoIds.size} / {allPhotosInBatch.length} selected):</Typography>
            <Paper variant="outlined" sx={{ p: 1, overflowY: 'auto', flexGrow: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
              {loadingPhotos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                allPhotosInBatch.map(photo => {
                  const stablePhotoId = getStablePhotoId(photo); // photo is from allPhotosInBatch
                  return (
                    <Box key={stablePhotoId} sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => handleTogglePhoto(photo)}>
                      <img
                        src={photo.baseUrl || photo.url} // photos from fetchMultipleEntriesByIds have baseUrl
                        alt={photo.filename || `Photo ${photo.id}`} // Use photo.id (GPhotosID/FirestoreID for Drive obj)
                        style={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          border: selectedPhotoIds.has(stablePhotoId) ? '3px solid #1976d2' : '3px solid transparent',
                          borderRadius: 4,
                        }}
                      />
                      <Checkbox
                        checked={selectedPhotoIds.has(stablePhotoId)}
                        // onChange={() => handleTogglePhoto(photo)} // Click on Box already handles this
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          color: '#fff',
                          backgroundColor: selectedPhotoIds.has(stablePhotoId) ? 'rgba(25, 118, 210, 0.7)' : 'rgba(0,0,0,0.4)',
                          '&.Mui-checked': {
                            color: '#fff',
                            backgroundColor: 'rgba(25, 118, 210, 0.7)',
                          },
                          padding: '2px'
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Paper>
          </Grid>
        </Grid>
        <TextField
          label="Additional instructions for AI (optional)"
          multiline
          fullWidth
          rows={2}
          value={userInstructions}
          onChange={e => setUserInstructions(e.target.value)}
          sx={{ mt: 2, flexShrink: 0 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleRegenerateClick} disabled={selectedPhotoIds.size === 0 || loadingPhotos}>
          Regenerate Layout Set
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LayoutSetEditPopup;
